import { latLngToLocalMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmLanduseArea, TerrainCover } from '@roadworld/shared';

/** RGB palette (0..255) used to bake the landcover texture. */
export const COVER_PALETTE: Record<TerrainCover, [number, number, number]> = {
  grass: [127, 176, 105], // #7FB069
  scrub: [157, 176, 122], // #9DB07A
  farmland: [217, 201, 122], // #D9C97A
  sand: [232, 217, 168], // #E8D9A8
  water: [95, 181, 220], // #5FB5DC — more saturated cartoon blue
  rock: [126, 116, 104], // #7E7468 — darker stone gray
  forest: [79, 138, 62], // #4F8A3E — deeper forest green
};

/**
 * Default cover when no OSM polygon claims a pixel — slightly desaturated grass that
 * the slope/elevation overlay will paint rocky on steep ground anyway.
 */
const DEFAULT_COVER: [number, number, number] = COVER_PALETTE.grass;

/** Lower number = painted first; higher cover overrides lower at overlaps. */
const COVER_PRIORITY: Record<TerrainCover, number> = {
  grass: 0,
  farmland: 1,
  scrub: 2,
  sand: 3,
  forest: 4,
  rock: 5,
  water: 6,
};

export interface LandcoverResult {
  /** RGBA8 texture data, `resolution`×`resolution`, row-major. */
  rgba: Uint8Array;
  resolution: number;
}

/**
 * Rasterize the chunk's landuse polygons into a square RGBA texture covering the
 * chunk's footprint (chunk-local coords from -size/2 .. +size/2). Pixels with no
 * polygon get the default grass color.
 */
export function rasterizeCover(payload: ChunkPayload, resolution: number): LandcoverResult {
  const rgba = new Uint8Array(resolution * resolution * 4);
  // Initialize to default.
  for (let i = 0; i < resolution * resolution; i++) {
    rgba[i * 4 + 0] = DEFAULT_COVER[0];
    rgba[i * 4 + 1] = DEFAULT_COVER[1];
    rgba[i * 4 + 2] = DEFAULT_COVER[2];
    rgba[i * 4 + 3] = 255;
  }

  // Defensive: older server responses (or stale disk caches that pre-date the visual-overhaul
  // ChunkPayload extension) don't include landuseAreas at all. Treat that as "no extra
  // landcover info" rather than crashing the whole chunk build.
  const areas = payload.osm.landuseAreas ?? [];
  const sorted = [...areas].sort(
    (a, b) => COVER_PRIORITY[a.cover] - COVER_PRIORITY[b.cover],
  );

  for (const area of sorted) {
    const outerRing = ringToLocal(area.outer, payload);
    if (outerRing.length < 3) continue;
    const innerRings = (area.inners ?? [])
      .map((ids) => ringToLocal(ids, payload))
      .filter((r) => r.length >= 3);
    fillPolygon(rgba, resolution, payload.sizeMeters, outerRing, innerRings, COVER_PALETTE[area.cover]);
  }

  return { rgba, resolution };
}

function ringToLocal(nodeIds: number[], payload: ChunkPayload): Array<{ x: number; z: number }> {
  const ring: Array<{ x: number; z: number }> = [];
  for (const id of nodeIds) {
    const node = payload.osm.nodes[id];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
    ring.push({ x, z });
  }
  return ring;
}

/**
 * Even-odd scanline polygon fill, with holes (inner rings). Coordinates are in
 * chunk-local meters; we map (-half..+half) → (0..resolution-1) pixel space.
 */
function fillPolygon(
  rgba: Uint8Array,
  resolution: number,
  sizeMeters: number,
  outer: Array<{ x: number; z: number }>,
  inners: Array<Array<{ x: number; z: number }>>,
  color: [number, number, number],
): void {
  const half = sizeMeters * 0.5;
  const toPx = (m: number) => ((m + half) / sizeMeters) * (resolution - 1);

  // Find scanline range in pixel rows (clamped to chunk).
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of outer) {
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  const rowMin = Math.max(0, Math.floor(toPx(minZ)));
  const rowMax = Math.min(resolution - 1, Math.ceil(toPx(maxZ)));

  const allRings = [outer, ...inners];

  for (let row = rowMin; row <= rowMax; row++) {
    // Pixel row → world z.
    const worldZ = (row / (resolution - 1)) * sizeMeters - half;
    const intersections: number[] = [];
    for (const ring of allRings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i]!;
        const b = ring[j]!;
        const crosses = a.z > worldZ !== b.z > worldZ;
        if (!crosses) continue;
        const t = (worldZ - a.z) / (b.z - a.z || 1e-9);
        const x = a.x + t * (b.x - a.x);
        intersections.push(toPx(x));
      }
    }
    intersections.sort((p, q) => p - q);
    for (let k = 0; k + 1 < intersections.length; k += 2) {
      const xs = Math.max(0, Math.ceil(intersections[k]!));
      const xe = Math.min(resolution - 1, Math.floor(intersections[k + 1]!));
      for (let x = xs; x <= xe; x++) {
        const i = (row * resolution + x) * 4;
        rgba[i + 0] = color[0];
        rgba[i + 1] = color[1];
        rgba[i + 2] = color[2];
      }
    }
  }
}

/** Re-export the OsmLanduseArea type for client convenience. */
export type { OsmLanduseArea };
